import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Avatar,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";

const TransferOwnershipDialog = ({
  open,
  onClose,
  subUsers,
  onTransfer,
  showError,
  showSuccess,
}) => {
  const [selectedUid, setSelectedUid] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const activeSubUsers = (subUsers || []).filter((u) => {
    const status = u.status ?? u.user_auth?.status;
    return status === "active";
  });

  const handleTransfer = async () => {
    if (!selectedUid) return;
    setSubmitting(true);
    try {
      await onTransfer({
        new_owner_id: selectedUid,
        reason: "User requested to transfer ownership.",
      });
      showSuccess(
        "Ownership has been transferred. Your role has been updated to viewer.",
      );
      onClose(true);
      setTimeout(() => window.location.reload(), 1000);
    } catch (err) {
      showError(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setSelectedUid(null);
      onClose(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='sm'
      fullWidth
      sx={{ boxShadow: 24, bgcolor: "background.paper", borderRadius: 3 }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: 1,
          borderColor: "divider",
          py: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <SwapHorizIcon color='primary' />
          <Typography variant='h6' fontWeight={700} color='text.primary'>
            Transfer ownership
          </Typography>
        </Box>
        <IconButton onClick={handleClose} disabled={submitting} size='small'>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <br />
        <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
          Select a sub-user to become the new account owner. The selected user
          will become owner and you will become a viewer.
        </Typography>
        {activeSubUsers.length === 0 ? (
          <Typography color='text.secondary'>
            No active sub-users available to transfer ownership to.
          </Typography>
        ) : (
          <List dense disablePadding>
            {activeSubUsers.map((user) => {
              const uid = user.u_id;
              const selected = selectedUid === uid;
              return (
                <ListItemButton
                  key={uid}
                  selected={selected}
                  onClick={() => setSelectedUid(uid)}
                  sx={{ borderRadius: 1, mb: 0.5 }}
                >
                  <ListItemAvatar>
                    <Avatar
                      sx={{
                        bgcolor: selected ? "primary.main" : "grey.400",
                        fontSize: "1rem",
                      }}
                    >
                      {(user.name || user.email || "?").charAt(0).toUpperCase()}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={user.name || user.email}
                    secondary={user.email}
                    primaryTypographyProps={{ fontWeight: 500 }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        )}
      </DialogContent>
      <DialogActions
        sx={{ px: 3, py: 2, borderTop: 1, borderColor: "divider" }}
      >
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant='contained'
          color='primary'
          onClick={handleTransfer}
          disabled={!selectedUid || submitting || activeSubUsers.length === 0}
          startIcon={
            submitting ? (
              <CircularProgress size={18} color='inherit' />
            ) : (
              <SwapHorizIcon />
            )
          }
        >
          {submitting ? "Transferring…" : "Transfer ownership"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TransferOwnershipDialog;
